import React, { ForwardedRef } from 'react';
import clsx from 'clsx';
import { AriaButtonProps } from 'react-aria/useButton';
import { useFocusRing } from 'react-aria/useFocusRing';
import { useHover } from 'react-aria/useHover';
import styles from '@danske/sapphire-css/components/tabs/tabs.module.css';

import { TabProps } from '../TabsProps';
import { TabElementType } from './UnderlinedTabsList';
import { mergeNonNullProps } from '../../../utils';

export interface UnderlinedTabBaseProps<T extends 'a' | 'div'>
  extends Omit<
      AriaButtonProps<T>,
      | 'children'
      | 'onBlur'
      | 'onKeyDown'
      | 'onKeyUp'
      | 'onClick'
      | 'target'
      | 'type'
      | 'onFocus'
    >,
    TabProps {}

export const UnderlinedTabBase = React.forwardRef(function UnderlinedTabBase<
  T extends 'a' | 'div'
>(
  {
    isDisabled,
    isActive,
    children,
    onClick,
    elementType,
    ...props
  }: UnderlinedTabBaseProps<T>,
  ref: ForwardedRef<TabElementType>
): JSX.Element {
  const ElementType = elementType || ((props.href ? 'a' : 'div') as string);
  const { hoverProps, isHovered } = useHover({ isDisabled });
  const { focusProps, isFocusVisible } = useFocusRing(props);

  return (
    <ElementType
      {...mergeNonNullProps(props, hoverProps, focusProps)}
      ref={ref}
      onClick={isDisabled ? undefined : onClick}
      href={isDisabled ? undefined : props.href}
      className={clsx(
        styles['sapphire-tabs__tab'],
        styles['js-hover'],
        styles['js-focus'],
        {
          [styles['sapphire-tabs__tab--active']]: isActive,
          [styles['is-hover']]: isHovered,
          [styles['is-focus']]: isFocusVisible,
          [styles['is-disabled']]: isDisabled,
        }
      )}
    >
      <span className={styles['sapphire-tabs__label']}>{children}</span>
    </ElementType>
  );
});
